import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { setActiveChallengeId } from '../hooks/challenge/activeChallenge'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { useEntitledChallengeIds } from '../hooks/membership/useEntitledChallengeIds'
import { useTeamMemberships } from '../hooks/membership/useTeamMemberships'
import { challengeTeamPath, ScreenPath } from '../navigation'
import { PassType } from '../service/domain/PaymentService'
import { logError } from '../util/errorLogger'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import Spinner from '../ui/primitives/Spinner'
import styles from '../ui/team/TeamEntry.module.css'

export interface PaymentAction {
  type: 'CREATE_TEAM' | 'JOIN_TEAM' | 'FREE_AGENT'
  payload: Record<string, unknown>
}

export default function PaymentScreen() {
  const [searchParams] = useSearchParams()
  const { challengeId: routeChallengeId } = useParams()
  const navigate = useNavigate()
  const passType = searchParams.get('passType') as PassType | undefined
  const actionParam = searchParams.get('action')

  let action: PaymentAction | undefined
  try {
    action = actionParam ? (JSON.parse(actionParam) as PaymentAction) : undefined
  } catch (error) {
    void logError(error, { screen: 'PaymentSuccess', actionParam: actionParam ?? undefined })
    throw new Error('Invalid action parameter')
  }

  if (!passType) {
    void logError(new Error('No pass type provided'), {
      screen: 'PaymentSuccess',
      actionParam: actionParam ?? undefined,
    })
    throw new Error('No pass type provided')
  }
  if (!action) {
    void logError(new Error('No action provided'), {
      screen: 'PaymentSuccess',
      passType,
      actionParam: actionParam ?? undefined,
    })
    throw new Error('No action provided')
  }

  const {
    challengeId,
    isEntitled,
    teamID,
    isLoading: entitlementLoading,
    refetchEntitlement,
  } = useChallengeAccess(routeChallengeId)
  const { refetch: refetchMemberships } = useTeamMemberships()
  const { refetch: refetchEntitledIds } = useEntitledChallengeIds()

  const needsTeam = action.type === 'CREATE_TEAM'
  const waitingForEntitlement = !isEntitled
  const waitingForTeam = needsTeam && !teamID

  useEffect(() => {
    refetchEntitlement()
    refetchMemberships()
    refetchEntitledIds()
    const interval = setInterval(() => {
      refetchEntitlement()
      refetchMemberships()
      refetchEntitledIds()
    }, 2000)
    return () => clearInterval(interval)
  }, [refetchEntitlement, refetchMemberships, refetchEntitledIds])

  async function onContinue() {
    if (challengeId) setActiveChallengeId(challengeId)

    await refetchEntitlement()
    const { data: memberships } = await refetchMemberships()
    const membershipForChallenge =
      memberships?.find((m) => m.challengeID === challengeId)?.teamID ?? ''
    const resolvedTeamId = teamID || membershipForChallenge

    if (action?.type === 'CREATE_TEAM') {
      if (resolvedTeamId) {
        navigate(`${ScreenPath.team}/${resolvedTeamId}`, { replace: true })
      } else {
        void logError(new Error('Team not created yet by webhook'), {
          screen: 'PaymentSuccess',
          actionType: action?.type,
        })
        navigate(challengeTeamPath(challengeId), { replace: true })
      }
    } else if (action?.type === 'JOIN_TEAM') {
      const { teamId } = action.payload as { teamId: string }
      navigate(`${ScreenPath.team}/${teamId}`, { replace: true })
    } else if (action?.type === 'FREE_AGENT') {
      navigate(ScreenPath.home, { replace: true })
    } else {
      navigate(ScreenPath.home, { replace: true })
    }
  }

  const backTo = challengeId ? challengeTeamPath(challengeId) : ScreenPath.challenges

  if (entitlementLoading || waitingForEntitlement) {
    return (
      <SimplePageLayout title="Verifying payment…" backTo={backTo}>
        <div className={styles.statusCard}>
          <Spinner />
          <p className={styles.statusCopy}>Please wait while we verify your payment…</p>
        </div>
      </SimplePageLayout>
    )
  }

  if (waitingForTeam) {
    return (
      <SimplePageLayout title="Setting up team…" backTo={backTo}>
        <div className={styles.statusCard}>
          <Spinner />
          <p className={styles.statusCopy}>Please wait while we set up your team…</p>
        </div>
      </SimplePageLayout>
    )
  }

  const isButtonDisabled = needsTeam ? !teamID : !isEntitled

  return (
    <SimplePageLayout title="Payment successful" backTo={backTo}>
      <div className={styles.statusCard}>
        <p className={styles.statusCopy}>
          You did it! You&apos;re officially in. Let&apos;s do this thing.
        </p>
        <div className={styles.statusActions}>
          <Button onClick={onContinue} disabled={isButtonDisabled}>
            Let&apos;s go!
          </Button>
        </div>
      </div>
    </SimplePageLayout>
  )
}
